/**
 * Created by calhosh on 3/30/2026
 */
import {
	misstatementPeriodTypes,
	MisstatementType,
	ParentMisstatementType,
	MisstatementStatusId,
	extendedPagingOptions,
	clientSideEventNames
} from '../../../../../../../util/uiconstants';
import {currentResource} from '../../../../../../../util/utils';

export const filterMisstatementType = {
	sad: 1,
	incomeEffect: 2,
	reclassification: 3,
	cashflow: 4,
	disclosure: 5,
	manualAdjustment: 6
};

export const parentMisstatementType = {
	parentIncomeStatement: 7,
	parentReclassification: 8,
	parentCashFlow: 9,
	parentDisclosure: 10,
	parentManualTopUpAdjustment: 11
};

export const sadSummaryMaterialityViewTypes = {
	uncorrected: 3,
	corrected: 2,
	clearlyTrivial: 4
};

export const misstatementPeriodTypeId = {
	currentYear: 1,
	previousYear: 2
};

// Misstatement calculation constants
export const analysisMisstatementPeriodTypeId = 1;
export const comparativeMisstatementPeriodTypeId = 2;
export const grossAmountEligibleFsTypeIds = [11, 20, 21];

// Deficiency relationship helpers
export const relatedDeficiencyState = {
	related: 'related',
	noRelated: 'noRelated',
	hidden: 'hidden'
};

// ---------------------------------------------------------------------------
// SadSummary304 container helpers & constants
// (extracted from SadSummary304.js to keep the container component lean)
// ---------------------------------------------------------------------------

export const defaultCheckbox = {yes: true, no: true};
export const emptyListData = {data: [], totalEntityCount: 0};
export const emptyIdMap = {};

export const deletedPriorPeriodMisstatementStatusIds = [MisstatementStatusId.priorPeriodRolledForward, MisstatementStatusId.priorPeriodAdjusted];

export const metricIdToParentMisstatementType = {
	[filterMisstatementType.incomeEffect]: parentMisstatementType.parentIncomeStatement,
	[filterMisstatementType.reclassification]: parentMisstatementType.parentReclassification,
	[filterMisstatementType.cashflow]: parentMisstatementType.parentCashFlow,
	[filterMisstatementType.disclosure]: parentMisstatementType.parentDisclosure,
	[filterMisstatementType.manualAdjustment]: parentMisstatementType.parentManualTopUpAdjustment
};

export const misstatementTableConfigsBase = [
	{labelKey: 'withEffectOnIncome', filterType: filterMisstatementType.incomeEffect, tileId: 'withIncomeEffect'},
	{labelKey: 'withNoIncomeEffect', filterType: filterMisstatementType.reclassification, tileId: 'withoutIncomeEffect'},
	{labelKey: 'cashFlowMisstatements', filterType: filterMisstatementType.cashflow, tileId: 'cashflow'},
	{labelKey: 'discloseMisstatements', filterType: filterMisstatementType.disclosure, tileId: 'disclosure'}
];

// ---------------------------------------------------------------------------
// SAD export modal constants (moved from SADExportModal/sadExportConstants.js)
// ---------------------------------------------------------------------------

export const sadExportTypes = {
	exportAll: 'exportAll',
	exportForManagement: 'exportForManagement',
	exportForTCWG: 'exportForTCWG'
};

export const sadOutputFormat = {
	combined: 'combined',
	separate: 'separate'
};

export const sadPrintCategory = {
	[sadExportTypes.exportAll]: 1,
	[sadExportTypes.exportForManagement]: 2,
	[sadExportTypes.exportForTCWG]: 3
};

export const sadPrintType = {
	consolidated: 1,
	separated: 2
};

export const maxSearchLength = 100;
export const searchDebounceMs = 300;

export function getTabSwitchData() {
	return [
		{
			id: sadSummaryMaterialityViewTypes.uncorrected,
			name: currentResource?.SadSummary304?.uncorrected,
			ariaLabel: currentResource?.SadSummary304?.uncorrected
		},
		{
			id: sadSummaryMaterialityViewTypes.corrected,
			name: currentResource?.SadSummary304?.corrected,
			ariaLabel: currentResource?.SadSummary304?.corrected
		}
	];
}

export function getPeriodTypeDisplayName(periodTypeId) {
	const periodTypeLabels = currentResource?.MisstatementDetails?.periodTypeLabels;

	switch (periodTypeId) {
		case misstatementPeriodTypes.currentPeriod:
			return periodTypeLabels?.currentPeriod;
		case misstatementPeriodTypes.comparativePeriod:
			return periodTypeLabels?.comparativePeriod;
		case misstatementPeriodTypes.secondComparativePeriod:
			return periodTypeLabels?.secondComparativePeriod;
		default:
			return null;
	}
}

export function getMisstatementTypeDisplayName(misstatementTypeId) {
	const misstatementTypeLabels = currentResource?.MisstatementDetails?.misstatementTypeLabels;

	switch (misstatementTypeId) {
		case MisstatementType.factual:
			return misstatementTypeLabels?.factual;
		case MisstatementType.projected:
			return misstatementTypeLabels?.projected;
		case MisstatementType.judgemental:
			return misstatementTypeLabels?.judgemental;
		case MisstatementType.reclassification:
			return misstatementTypeLabels?.reclassification;
		case MisstatementType.clientAdjustment:
			return misstatementTypeLabels?.clientAdjustment;
		case MisstatementType.manualTopUpAdjustment:
			return misstatementTypeLabels?.manualTopUpAdjustment;
		case MisstatementType.notSelected:
			return misstatementTypeLabels?.notSelected;
		default:
			return null;
	}
}

export function getParentMisstatementTypeDisplayName(parentMisstatementTypeId) {
	const parentMisstatementTypeLabels = currentResource?.MisstatementDetails?.parentMisstatementTypeLabels;

	switch (parentMisstatementTypeId) {
		case ParentMisstatementType.hasEffectOnIncome:
			return parentMisstatementTypeLabels?.hasEffectOnIncome;
		case ParentMisstatementType.doesNotHaveEffectOnIncome:
			return parentMisstatementTypeLabels?.doesNotHaveEffectOnIncome;
		case ParentMisstatementType.cashFlow:
			return parentMisstatementTypeLabels?.cashFlow;
		case ParentMisstatementType.disclosure:
			return parentMisstatementTypeLabels?.disclosure;
		default:
			return null;
	}
}

export function getAllMisstatementsForMetrics(metrics, activeTab) {
	if (!metrics) {
		return 0;
	}

	return activeTab === sadSummaryMaterialityViewTypes.corrected ? (metrics?.countCorrectedMisstatement ?? 0) : (metrics?.countUncorrectedMisstatement ?? 0);
}

// Returns the total count for the currently selected tile. When the "all"
// (sad) tile is selected this falls back to the full corrected/uncorrected
// total; otherwise it returns the per-category count that backs the selected
// tile so downstream calculations (e.g. quick-filter "No" counts) are scoped
// to that subset instead of the full tab total.
export const getSelectedTileCount = (metrics, selectedMetricId, activeTab) => {
	if (!metrics) {
		return 0;
	}

	switch (selectedMetricId) {
		case filterMisstatementType.incomeEffect:
			return metrics?.countIncomeStatementMisstatement ?? 0;
		case filterMisstatementType.reclassification:
			return metrics?.countReclassMisstatement ?? 0;
		case filterMisstatementType.cashflow:
			return metrics?.countCashFlowMisstatement ?? 0;
		case filterMisstatementType.disclosure:
			return metrics?.countDisclosureMisstatement ?? 0;
		case filterMisstatementType.sad:
		default:
			return getAllMisstatementsForMetrics(metrics, activeTab);
	}
};

// Misstatement calculation functions
export function calculateGrossAmount(item) {
	if (item.misstatementPeriodTypeId !== analysisMisstatementPeriodTypeId) {
		return 0;
	}
	const value = item?.adjustmentTotals?.analysisPeriodAdjustmentAmount;
	if (value === null || value === undefined) {
		return 0;
	}

	return Number(value);
}

export function calculateNetAmountAfterTurnaround(item) {
	if (item.misstatementPeriodTypeId === analysisMisstatementPeriodTypeId) {
		// CY or CY/PY misstatement: gross amount + turnaroundAdjustmentAmount from comparative children
		const grossAmt = calculateGrossAmount(item);

		// If childMisstatements exist, sum turnaroundAdjustmentAmount from their adjustmentTotals
		const hasChildren = item.childMisstatements && Array.isArray(item.childMisstatements) && item.childMisstatements.length > 0;
		if (hasChildren) {
			const childTurnaroundSum = item.childMisstatements.reduce((sum, child) => {
				const turnaroundAmount = child?.adjustmentTotals?.turnaroundAdjustmentAmount || 0;
				return sum + turnaroundAmount;
			}, 0);
			return grossAmt + childTurnaroundSum;
		}

		// Fallback to grossAmount
		return grossAmt;
	}
	if (item.misstatementPeriodTypeId === comparativeMisstatementPeriodTypeId) {
		// PY misstatement: use turnaroundAdjustmentAmount from adjustmentTotals
		return item?.adjustmentTotals?.turnaroundAdjustmentAmount || 0;
	}
	return 0;
}

// Multi-entity helpers
export function getEntityIdFromMisstatement(multiEntities) {
	const entity = (multiEntities ?? [])[0];
	return entity?.stEntityId ?? entity?.id ?? entity?.entityId ?? null;
}

export function getRelatedDeficienciesCount(relatedDeficiencies) {
	if (!Array.isArray(relatedDeficiencies)) {
		return 0;
	}
	return relatedDeficiencies.length;
}

export function hasDeficiencyRationaleValue(deficiencyRationale) {
	if (deficiencyRationale === null || deficiencyRationale === undefined) {
		return false;
	}
	if (typeof deficiencyRationale === 'string') {
		return deficiencyRationale.trim().length > 0;
	}
	return true;
}

export function hasDisclosureEvaluationRationale(evaluationRationale) {
	if (evaluationRationale === null || evaluationRationale === undefined) {
		return false;
	}
	if (typeof evaluationRationale === 'string') {
		return evaluationRationale.trim().length > 0;
	}
	return true;
}

export function getMisstatementRelatedDeficiencyState(misstatement, hasMisstatementRelatedDeficiencyOption) {
	if (!hasMisstatementRelatedDeficiencyOption) {
		return relatedDeficiencyState.hidden;
	}

	if (getRelatedDeficienciesCount(misstatement?.deficiencies) > 0) {
		return relatedDeficiencyState.related;
	}

	if (hasDeficiencyRationaleValue(misstatement?.deficiencyRationale)) {
		return relatedDeficiencyState.noRelated;
	}

	return relatedDeficiencyState.hidden;
}

export function getDefaultPagingByCategory() {
	return {
		[filterMisstatementType.incomeEffect]: {page: 1, pageSize: extendedPagingOptions.options[0]},
		[filterMisstatementType.reclassification]: {page: 1, pageSize: extendedPagingOptions.options[0]},
		[filterMisstatementType.cashflow]: {page: 1, pageSize: extendedPagingOptions.options[0]},
		[filterMisstatementType.disclosure]: {page: 1, pageSize: extendedPagingOptions.options[0]}
	};
}

export function getDefaultExpandState() {
	return {
		mode: 'none',
		expandedIds: {},
		collapsedIds: {},
		commandToken: 0
	};
}

export function getDefaultFilterState() {
	return {filters: undefined};
}

export function filterReducer(state, action) {
	switch (action.type) {
		case 'state/set':
			return {...state, ...action.value};
		default:
			return state;
	}
}

export function hasYesNoFilterSelection(filterSelection) {
	return Boolean(filterSelection) && (!filterSelection.yes || !filterSelection.no);
}

export function normalizeMisstatementId(misstatementId) {
	return misstatementId === null || misstatementId === undefined ? null : String(misstatementId);
}

export function getMisstatementIdFromSearch(search) {
	const params = new URLSearchParams(search);
	return normalizeMisstatementId(params.get('misstatementid'));
}

export function refreshValidationData() {
	window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
}

// Resolves a {yes, no} quick-filter selection into a single backend boolean
// param, or `{}` when both/neither are selected (i.e. no filtering).
// When `invert` is true the boolean is flipped (used for communication where the
// backend's filterHasCommunication=true means communicationRationale IS NULL).
function resolveYesNoFilterParam(key, filter, invert = false) {
	if (filter?.yes && !filter?.no) return {[key]: !invert};
	if (!filter?.yes && filter?.no) return {[key]: invert};
	return {};
}

// Builds the shared query params for the listMisstatements calls. Callers pass
// the currently-resolved values (from state or refs) so behaviour is identical
// regardless of call site.
export function buildMisstatementListQueryParams({
	searchString,
	filterHasRelatedDeficiency,
	filterHasCommunication,
	filterHasMyReview,
	filterCorrected,
	bodyStEntityId,
	shouldShowGroupComponent,
	selectedGroupComponentIds,
	relatedAccountsValue,
	misstatementStatusValue,
	misstatementTypeValue
}) {
	return {
		searchString,
		includeMisstatementAdjustments: true,
		includePagination: true,
		includeChildMisstatement: true,
		groupChildMisstatement: true,
		includeRelatedDeficiency: true,
		includeRelatedAccount: true,
		...resolveYesNoFilterParam('filterHasRelatedDeficiency', filterHasRelatedDeficiency),
		// backend's filterHasCommunication=true means communicationRationale IS NULL
		...resolveYesNoFilterParam('filterHasCommunication', filterHasCommunication, true),
		...resolveYesNoFilterParam('filterHasMyReview', filterHasMyReview),
		filterCorrected,
		...(bodyStEntityId !== null && typeof bodyStEntityId === 'number' && bodyStEntityId > 0 ? {filterRelatedMultiEntities: [bodyStEntityId]} : {}),
		...(shouldShowGroupComponent ? {filterRelatedComponents: selectedGroupComponentIds} : {}),
		...(Array.isArray(relatedAccountsValue) && relatedAccountsValue.length > 0 ? {filterRelatedAccounts: relatedAccountsValue} : {}),
		...(Array.isArray(misstatementStatusValue) && misstatementStatusValue.length > 0 ? {filterMisstatementStatus: misstatementStatusValue} : {}),
		...(Array.isArray(misstatementTypeValue) && misstatementTypeValue.length > 0 ? {filterMisstatementTypes: misstatementTypeValue} : {}),
		orderBy: 'MisstatementIndex'
	};
}
